<template>
  <px-custom-dialog v-model="dialog"
                    content-class="rounded-card"
                    :width="dialogWidth">
    <template v-slot:activator="{ on }">
      <slot name="activator" v-bind:on="on">
        <v-btn icon
               class="rounded-tag-small normal-btn-text elevation-0"
               @click="dialog = true">
          <v-icon>
            mdi-arrow-split-vertical
          </v-icon>
        </v-btn>
      </slot>
    </template>
    <template v-slot:title>
      Dividir saldo
    </template>
    <template>
      <div class="text--primary">
        <v-row dense class="mt-3">
          <v-col cols="12">
            <span class="title font-weight-bold">
              {{ saldoPendente }} UN pendentes
            </span>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <span>
              Divida o saldo pendente em partes menores, informando a quantidade e as datas de
              previsão de faturamento e entrega.
            </span>
          </v-col>
        </v-row>
        <v-row v-if="errorMessage" class="error--text">
          <v-col cols="12">
            {{ errorMessage }}
          </v-col>
        </v-row>
        <v-list v-if="saldosEdicao.length">
          <v-list-item v-for="(saldo, index) in saldosEdicao" :key="saldo.id || saldo.$$id">
            <v-list-item-content class="px-1">
              <px-input-decimal-number
                dense
                required
                ref="quantidade"
                label="Quantidade"
                class="required rounded-tag-small"
                :precision="2"
                :error="$v.saldosEdicao.$each[index].saldo.$error"
                @blur="$v.saldosEdicao.$each[index].saldo.$touch()"
                v-model="saldo.saldo"/>
            </v-list-item-content>
            <v-list-item-content class="px-1">
              <px-date-picker dense
                              class="required"
                              input-class="rounded-tag-small"
                              label="Previsão de faturamento"
                              :error="$v.saldosEdicao.$each[index].dataPrevisaoFaturamento.$error"
                              @blur="$v.saldosEdicao.$each[index].dataPrevisaoFaturamento.$touch()"
                              v-model="saldo.dataPrevisaoFaturamento"/>
            </v-list-item-content>
            <v-list-item-content class="px-1">
              <px-date-picker dense
                              class="required"
                              input-class="rounded-tag-small"
                              label="Previsão de entrega"
                              :error="$v.saldosEdicao.$each[index].dataPrevisaoEntrega.$error"
                              @blur="$v.saldosEdicao.$each[index].dataPrevisaoEntrega.$touch()"
                              v-model="saldo.dataPrevisaoEntrega"/>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn icon
                     small
                     :disabled="saldosEdicao.length === 1"
                     @click="removerSaldo(index)">
                <v-icon small>
                  mdi-close
                </v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <v-row>
          <v-col cols="12" class="text-right">
            <v-btn small
                   color="primary"
                   class="rounded-tag-small elevation-0 normal-btn-text"
                   @click="onAddSaldo">
              Dividir saldo
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </template>
    <template v-slot:actions>
      <v-btn class="elevation-0 rounded-tag-small normal-btn-text"
             color="primary"
             :loading="loading"
             @click="onClickSave"
      >
        Salvar
      </v-btn>
      <v-btn class="elevation-0 rounded-tag-small normal-btn-text"
             @click="cancel">
        Cancelar
      </v-btn>
    </template>
  </px-custom-dialog>
</template>

<script>
import { SituacaoGestaoEntregaProdutoSaldo } from 'px-business-components';
import { sum, cloneDeep, uniqueId } from 'lodash';
import GestaoEntregaService from '@/modules/gestao-entregas/gestao-entrega-service';
import validationMixin from '@/mixins/validation-mixin';
import { required } from 'vuelidate/lib/validators';

const initialState = (previsaoFaturamento, previsaoEntrega) => ({
  $$id: `saldo-${uniqueId()}`,
  saldo: 0,
  dataPrevisaoFaturamento: previsaoFaturamento,
  dataFaturamento: null,
  dataPrevisaoEntrega: previsaoEntrega,
  dataEntrega: null,
});

export default {
  mixins: [validationMixin],
  props: {
    compraProduto: {
      type: Object,
      required: true,
    },
    saldos: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      dialog: false,
      loading: false,
      errorMessage: null,
      saldosEdicao: [],
    };
  },
  computed: {
    isSmall() {
      return this.$vuetify.breakpoint.mdAndDown;
    },
    dialogWidth() {
      return this.isSmall ? '95%' : '800px';
    },
    saldosPendentes() {
      return this.saldos.filter(saldo => saldo.situacaoEntrega
        === SituacaoGestaoEntregaProdutoSaldo.keys.PENDENTE.key);
    },
    saldoPendente() {
      return sum(this.saldosPendentes
        .map(s => s.saldo));
    },
  },
  methods: {
    cancel() {
      this.dialog = false;
    },
    onAddSaldo() {
      const lastSaldo = this.saldosEdicao[this.saldosEdicao.length - 1];
      this.saldosEdicao.push(
        initialState(lastSaldo?.dataPrevisaoFaturamento, lastSaldo?.dataPrevisaoEntrega),
      );
    },
    removerSaldo(index) {
      this.saldosEdicao.splice(index, 1);
    },
    isSaldoValido() {
      const totalSaldo = sum(this.saldosEdicao.map(s => s.saldo));
      if (totalSaldo !== this.saldoPendente) {
        this.errorMessage = `A soma dos saldos deve ser igual a ${this.saldoPendente} ${this.compraProduto.unidadeMedida.descricao}.`;
        return false;
      }
      return true;
    },
    onClickSave() {
      this.errorMessage = null;
      if (!this.isSaldoValido()) {
        return;
      }
      if (this.validateForm()) {
        return;
      }
      this.loading = true;
      const payload = this.saldosEdicao.map(saldo => ({
        id: saldo.id ?? undefined,
        dataPrevisaoFaturamento: saldo.dataPrevisaoFaturamento,
        dataPrevisaoEntrega: saldo.dataPrevisaoEntrega,
        saldo: saldo.saldo,
      }));
      GestaoEntregaService.updateSaldosPendentes(this.compraProduto.id, payload)
        .then(({ data }) => {
          this.$toast('Saldos atualizados', { color: 'success' });
          this.$emit('change', data);
          this.cancel();
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  watch: {
    dialog(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }
      this.saldosEdicao = cloneDeep(this.saldosPendentes);
    },
  },
  validations: {
    saldosEdicao: {
      $each: {
        saldo: {
          required: true,
          minValue(value) {
            return value > 0;
          },
        },
        dataPrevisaoFaturamento: {
          required,
        },
        dataPrevisaoEntrega: {
          required,
        },
      },
    },
  },
};
</script>
